// mascot.ts - Nova's animated face in the widget (launcher, header, typing, welcome).
//
// One element per place; a mood change swaps the source on that same element, so
// nothing moves. The loops are served by the Nova API at /api/brand/:
//   loops/nova-{mood}-face-256.webm  transparent VP9 (Chrome, Firefox, Edge)
//   loops/nova-{mood}-face-128.webp  animated WebP (Safari and every iOS browser)
//   still/nova-{mood}-face-256.png   poster, and the picture for reduced motion
// The mascot is always animated; the still is used only for reduced motion or when
// the loop cannot play. When the host sets `avatarUrl`, that image is shown instead
// and moods do nothing.

export type NovaMood = 'idle' | 'thinking' | 'talking' | 'celebrating' | 'sleeping';

/** How long a one-off mood plays before going back (ms). */
export const CELEBRATE_MS = 1600;
export const TALK_MS = 1800;

export interface NovaMascotOptions {
  /** Nova API base (the widget's apiUrl) */
  apiUrl?: string;
  /** Host override: show this image instead of the mascot, no moods */
  customUrl?: string;
  mood?: NovaMood;
  /** Extra class on the wrapper */
  className?: string;
}

type RenderMode = 'video' | 'webp' | 'still' | 'custom';

let cachedMode: RenderMode | null = null;

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// Transparent VP9 WebM works in Chromium and Firefox. WebKit (Safari, and every
// browser on iOS) may answer canPlayType for VP9 yet drops the alpha channel, so it
// gets the animated WebP.
function loopMode(): RenderMode {
  if (cachedMode) return cachedMode;
  let mode: RenderMode = 'webp';
  try {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const webkitOnly = /AppleWebKit/i.test(ua) && !/(Chrome|Chromium|Edg|Android|Firefox)/i.test(ua);
    const video = document.createElement('video');
    const vp9 = typeof video.canPlayType === 'function' && video.canPlayType('video/webm; codecs="vp09.00.10.08"') !== '';
    if (vp9 && !webkitOnly) mode = 'video';
  } catch {
    mode = 'webp';
  }
  cachedMode = mode;
  return mode;
}

/** Only http(s), protocol-relative, root-relative or data:image URLs from the host. */
export function safeCustomUrl(url: unknown): string {
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (/^(https?:)?\/\//i.test(trimmed) || /^\/(?!\/)/.test(trimmed) || /^data:image\//i.test(trimmed)) return trimmed;
  return '';
}

export function brandBase(apiUrl?: string): string {
  return `${(apiUrl || 'https://nova.zeroauthoritydao.com').replace(/\/+$/, '')}/api/brand`;
}

export class NovaMascot {
  readonly el: HTMLSpanElement;
  private media: HTMLVideoElement | HTMLImageElement;
  private mode: RenderMode;
  private base: string;
  private mood: NovaMood;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: NovaMascotOptions = {}) {
    this.base = brandBase(options.apiUrl);
    this.mood = options.mood || 'idle';
    const custom = safeCustomUrl(options.customUrl);
    this.mode = custom ? 'custom' : prefersReducedMotion() ? 'still' : loopMode();

    this.el = document.createElement('span');
    this.el.className = `nova-mascot${options.className ? ` ${options.className}` : ''}`;
    this.el.setAttribute('aria-hidden', 'true');

    if (this.mode === 'video') {
      const video = document.createElement('video');
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('disablepictureinpicture', '');
      video.preload = 'auto';
      // A loop that cannot load falls back to the animated WebP.
      video.addEventListener('error', () => this.fallbackToWebp(), { once: true });
      this.media = video;
    } else {
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      if (this.mode === 'custom') img.src = custom;
      this.media = img;
    }
    this.media.className = 'nova-mascot__media';
    this.el.appendChild(this.media);
    if (this.mode !== 'custom') this.apply();
  }

  getMood(): NovaMood {
    return this.mood;
  }

  /** Switch mood now (cancels a one-off mood still playing). */
  setMood(mood: NovaMood): void {
    this.clearTimer();
    if (mood === this.mood) return;
    this.mood = mood;
    this.apply();
  }

  /** Play a mood for `ms`, then call `done` (which normally sets the next mood). */
  playOnce(mood: NovaMood, ms: number, done: () => void): void {
    this.setMood(mood);
    this.timer = setTimeout(() => {
      this.timer = null;
      done();
    }, ms);
  }

  /** Put the element where `slot` is (or append it) and make sure it is playing. */
  mount(slot: Element, replace = false): void {
    if (replace) slot.replaceWith(this.el);
    else slot.appendChild(this.el);
    this.resume();
  }

  /** A detached video pauses; call after the element is put back in the page. */
  resume(): void {
    if (this.media instanceof HTMLVideoElement && this.media.paused) {
      this.media.play()?.catch(() => { /* autoplay refused: the poster stays */ });
    }
  }

  destroy(): void {
    this.clearTimer();
    if (this.media instanceof HTMLVideoElement) {
      this.media.pause();
      this.media.removeAttribute('src');
      this.media.load();
    }
    this.el.remove();
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private stillUrl(mood: NovaMood): string {
    return `${this.base}/still/nova-${mood}-face-256.png`;
  }

  private apply(): void {
    const mood = this.mood;
    if (this.mode === 'custom') return;
    if (this.mode === 'video') {
      const video = this.media as HTMLVideoElement;
      video.poster = this.stillUrl(mood);
      video.src = `${this.base}/loops/nova-${mood}-face-256.webm`;
      this.resume();
    } else if (this.mode === 'webp') {
      (this.media as HTMLImageElement).src = `${this.base}/loops/nova-${mood}-face-128.webp`;
    } else {
      (this.media as HTMLImageElement).src = this.stillUrl(mood);
    }
  }

  private fallbackToWebp(): void {
    if (this.mode !== 'video') return;
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.className = 'nova-mascot__media';
    this.media.replaceWith(img);
    this.media = img;
    this.mode = 'webp';
    this.apply();
  }
}
